import styled from "@emotion/styled";
import { FC, ReactNode, useEffect, useRef } from "react";

import { Close } from "@livechat/design-system-icons";
import { Button, DesignToken, Icon, SpacingToken } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { useClickOutside } from "hooks/useClickOutside";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

interface IProps {
  opened: boolean;
  width?: string;
  onClose: () => void;
  children: ReactNode;
}

export const Drawer: FC<IProps> = ({ opened, onClose, width = "600px", children }) => {
  const drawerRef = useRef<HTMLDivElement>(null);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleClose = () => {
    if (opened) {
      const onboardingBar = document.getElementById("onboarding-bar");
      if (onboardingBar) {
        onboardingBar.classList.add("slide-bottom");
        setTimeout(() => {
          onboardingBar.classList.remove("slide-bottom");
        }, 450);
      }

      onClose();
    }
  };

  useClickOutside({ elementRef: drawerRef, onClickOutside: handleClose });

  useEffect(() => {
    if (opened && drawerRef.current) {
      drawerRef.current.scrollTo(0, 0);
    }
  }, [opened, drawerRef.current]);

  return (
    <Overlay opened={opened}>
      <Wrapper opened={opened} width={width} ref={drawerRef} data-testid="drawer">
        <Flex column gap={withVar(SpacingToken.Spacing4)}>
          <Flex justifyContent="flex-end">
            <CloseButton
              onClick={handleClose}
              size="compact"
              kind="plain"
              aria-label="close-drawer-button"
              data-testid="drawer-close-icon"
            >
              <Icon source={Close} />
            </CloseButton>
          </Flex>
          <Flex column>{children}</Flex>
        </Flex>
      </Wrapper>
    </Overlay>
  );
};

const Overlay = styled.div<{ opened: boolean }>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${withVar(DesignToken.SurfaceOtherOverlay)};
  z-index: ${StackingContextLevel.OverChatWidget};
  opacity: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (p: any) => (p.opened ? 1 : 0)
  };
  pointer-events: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (p: any) => (p.opened ? "all" : "none")
  };
  transition: opacity 0.2s ease-in-out;
`;

const Wrapper = styled.div<{ opened: boolean; width: string }>`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  overflow: auto;
  width: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (p: any) => (p.opened ? p.width : "0px")
  };
  background-color: var(${DesignToken.SurfacePrimaryDefault});
  z-index: ${StackingContextLevel.OverChatWidget};
  opacity: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (p: any) => (p.opened ? 1 : 0)
  };
  transition:
    width 0.2s ease,
    opacity 0.2s ease-in-out;
  padding: ${withVar(SpacingToken.Spacing4)} ${withVar(SpacingToken.Spacing6)};
  border-radius: 4px;
  border: 1px solid var(${DesignToken.BorderBasicSecondary});
  box-sizing: border-box;
`;

const CloseButton = styled(Button)`
  padding: 6px;
  border-radius: 50%;
`;
